import { Spinner, Flex, Box } from '@chakra-ui/react';
import { OfferDetails } from './components/OfferDetails';
import { OfferLetterTemplate } from './components/OfferLetterTemplate';
import { OfferApproval } from './components/OfferApproval';
import { OfferEmail } from './components/offer-email/OfferEmail';
import { useMemo, useEffect, useRef } from 'react';
import { useOfferInitiationConfig } from '@/components/ats/OfferInitiation/useOfferInitiationConfig';
import {
  useGetCustomFieldsQuery,
  useGetCompensationCurrenciesQuery,
  useGetAtsOfficesQuery,
  useGetAtsDepartmentsQuery,
  useGetOfferByIdQuery,
} from './api';
import { getInitializeOfferValues } from '@/components/ats/OfferInitiation/offerFormHelpers';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useApplicationHelper } from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
import { useOfferStore } from './store';

interface OfferBodyContainerProps {
  currentStep: any;
  childRefs: any;
  onTemplateSelect?: (
    _templateId: string,
    _templateValue: any
  ) => Promise<boolean>;
  type?: 'create' | 'edit';
}

export const OfferBodyContainer = ({
  currentStep,
  childRefs,
  onTemplateSelect,
  type,
}: OfferBodyContainerProps) => {
  const { applicationData: applicantData, isLoading: isApplicantFetching } =
    useApplicationHelper();

  const { currentJob = {} } = useSelector(getAtsJobCreate);
  const currUser = useSelector(selectCurrUser);
  const detailsFormRef = useRef(null);
  const offerId = useOfferStore((state) => state.offerId);
  const setOfferId = useOfferStore((state) => state.setOfferId);
  const signatureType = useOfferStore((state) => state.signatureType);
  const setOfferAttachments = useOfferStore(
    (state) => state.setOfferAttachments
  );

  // Fetch offer data if offerId is provided (for editing existing offer)
  const { data: currentOffer, isFetching: isOfferFetching } =
    useGetOfferByIdQuery(offerId, { staleTime: 0 });

  // Fetch custom fields data
  const { data: customFieldsData, isFetching: isCustomFieldsLoading } =
    useGetCustomFieldsQuery({ subject_type: 'ats_offer' }, { enabled: true });

  const { isFetching: isCurrenciesLoading } =
    useGetCompensationCurrenciesQuery();

  const { isFetching: isOfficesLoading } = useGetAtsOfficesQuery();

  const { isFetching: isDepartmentsLoading } = useGetAtsDepartmentsQuery();

  // Extract data from responses
  const offerStaticFields = useMemo(() => {
    return customFieldsData?.ats_form_template?.static_fields || [];
  }, [customFieldsData]);

  // Extract default values for additional compensation section
  const additionalCompensationDefaults = useMemo(() => {
    const additionalCompSection =
      customFieldsData?.ats_form_template?.static_fields?.find(
        (section) => section.id === 'additional_compensation'
      );
    return additionalCompSection?.default || {};
  }, [customFieldsData]);

  // Compute initialValues synchronously using useMemo to avoid race condition
  const initialValues = useMemo(() => {
    return getInitializeOfferValues({
      currentOffer,
      contactData: applicantData?.contact,
      currentJob: currentJob?.data,
      currUser,
      type,
      additionalCompensationDefaults,
    });
  }, [currentOffer, applicantData, currentJob?.data, currUser, type, additionalCompensationDefaults]);

  // Handle side effects for offerId and attachments separately
  useEffect(() => {
    if (currentOffer?.id) {
      setOfferId(currentOffer?.id || 0);
    }
    if (currentOffer?.attachments) {
      setOfferAttachments(currentOffer.attachments);
    }
  }, [currentOffer, setOfferId, setOfferAttachments]);

  // Determine overall loading state
  const isLoading = useMemo(() => {
    const loadingStates = [
      isCustomFieldsLoading,
      isCurrenciesLoading,
      isOfficesLoading,
      isDepartmentsLoading,
      isOfferFetching,
      isApplicantFetching,
    ];

    return loadingStates.some((loading) => loading);
  }, [
    isCustomFieldsLoading,
    isCurrenciesLoading,
    isOfficesLoading,
    isDepartmentsLoading,
    isOfferFetching,
    isApplicantFetching,
  ]);

  // Generate form config only when data is ready
  const { detailsFormConfig = [] } = useOfferInitiationConfig({
    tabIndex: 0,
    offerStaticFields,
    offerData: currentOffer,
    formRef: childRefs?.OFFER_DETAILS_COMP,
    isOfferV1: true,
  });

  // Show spinner while loading required data
  if (isLoading) {
    return (
      <Flex
        h="100%"
        w="100%"
        justifyContent="center"
        alignItems="center"
        minH="400px"
      >
        <Spinner color="primary-600" />
      </Flex>
    );
  }

  return (
    <>
      <Box display={currentStep?.id === 'offerDetails' ? 'block' : 'none'}>
        <OfferDetails
          ref={childRefs?.OFFER_DETAILS_COMP}
          initialValues={initialValues?.details || {}}
          detailsFormConfig={detailsFormConfig}
          detailsFormRef={detailsFormRef}
        />
      </Box>
      <Box
        display={currentStep?.id === 'offerLetterTemplate' ? 'flex' : 'none'}
        height="100%"
      >
        <OfferLetterTemplate
          ref={childRefs?.OFFER_LETTER_TEMPLATE_COMP}
          initialValues={initialValues?.templates || {}}
          detailsFormRef={detailsFormRef}
          detailsFormConfig={detailsFormConfig}
          currentOffer={currentOffer}
          onTemplateSelect={onTemplateSelect}
          currentStepID={currentStep?.id}
          initialDepartmentId={initialValues?.details?.ats_department_id}
          initialOfficeId={initialValues?.details?.ats_office_id}
        />
      </Box>
      <Box display={currentStep?.id === 'offerApproval' ? 'block' : 'none'}>
        <OfferApproval
          ref={childRefs?.OFFER_APPROVAL_COMP}
          type={type}
          currentOffer={currentOffer}
        />
      </Box>
      <Box display={currentStep?.id === 'offerEmail' ? 'block' : 'none'}>
        <OfferEmail
          ref={childRefs?.OFFER_EMAIL_COMP}
          type={type}
          currentOffer={currentOffer}
          selectSignatureType={signatureType}
        />
      </Box>
    </>
  );
};
